<template>
  <div class="worker-mobile-list">
    <!-- Edit mode -->
    <template v-if="editing">
      <div
        v-for="(row, ri) in editRows"
        :key="'m-edit-' + row.worker.id"
        class="worker-mobile-card"
        :class="{ 'worker-mobile-card-muted': !row.on_site }"
      >
        <div class="worker-mobile-head">
          <b-form-checkbox
            :checked="row.on_site"
            class="worker-on-site-toggle"
            @change="$emit('toggle-worker', ri, $event)"
          >
            <span class="worker-name">{{ row.worker.full_name }}</span>
          </b-form-checkbox>
          <span class="total-hours-badge">{{ rowTotal(row) }}</span>
        </div>
        <div v-if="row.worker.trade" class="worker-trade worker-mobile-trade">
          {{ formatTrade(row.worker.trade) }}
        </div>

        <div v-if="row.on_site" class="worker-mobile-days">
          <div
            v-for="(day, di) in row.days"
            :key="day.date"
            class="mobile-day-row mobile-day-row-edit"
            :class="{
              'mobile-day-row-active': day.present,
              'mobile-day-row-leave': isLeaveDay(day),
            }"
          >
            <div class="mobile-day-top">
              <div class="mobile-day-label">
                <span class="mobile-day-name">{{
                  weekDayHeaders[di].label
                }}</span>
                <span class="mobile-day-date">{{
                  weekDayHeaders[di].date
                }}</span>
              </div>
              <template v-if="!isLeaveDay(day)">
                <button
                  v-if="!day.present"
                  type="button"
                  class="day-add-btn mobile-day-add"
                  :aria-label="`Add ${row.worker.full_name} day ${di + 1}`"
                  @click="$emit('set-present', ri, di, true)"
                >
                  <b-icon-plus />
                  <span>Add day</span>
                </button>
                <button
                  v-else
                  type="button"
                  class="day-remove-btn"
                  :aria-label="`Remove ${row.worker.full_name} day ${di + 1}`"
                  @click="$emit('set-present', ri, di, false)"
                >
                  <b-icon-x />
                </button>
              </template>
            </div>

            <b-form-select
              :value="day.leave_type"
              :options="leaveTypeOptions"
              size="sm"
              class="leave-type-select"
              @change="$emit('set-leave-type', ri, di, $event)"
            />

            <template v-if="day.present && !isLeaveDay(day)">
              <div class="mobile-day-inputs">
                <label class="mobile-field">
                  <span>Start</span>
                  <datetime-input
                    :value="day.start"
                    type="time"
                    size="sm"
                    @input="$emit('set-day-field', ri, di, 'start', $event)"
                  />
                </label>
                <label class="mobile-field">
                  <span>Finish</span>
                  <datetime-input
                    :value="day.finish"
                    type="time"
                    size="sm"
                    @input="$emit('set-day-field', ri, di, 'finish', $event)"
                  />
                </label>
                <label class="mobile-field">
                  <span>Break</span>
                  <b-form-input
                    :value="day.break_minutes"
                    type="number"
                    min="0"
                    step="5"
                    size="sm"
                    placeholder="Mins"
                    @input="
                      $emit(
                        'set-day-field',
                        ri,
                        di,
                        'break_minutes',
                        Number($event),
                      )
                    "
                  />
                </label>
              </div>
              <div v-if="dayHoursLabel(day)" class="day-hours">
                {{ dayHoursLabel(day) }} worked
                <span v-if="day.break_minutes">
                  · {{ day.break_minutes }}m brk
                </span>
              </div>
              <div v-if="isNightDay(day)" class="day-night">
                <span class="day-night-badge">Night</span>
              </div>
              <div v-if="isOvernightDay(day)" class="day-overnight">
                Overnight - ends next morning
              </div>
              <div v-if="dayError(day)" class="day-error">
                {{ dayError(day) }}
              </div>
              <div
                v-if="hasOtherProjectConflict(day)"
                class="day-conflict"
                :title="day.other_project_name"
              >
                Also clocked in at {{ day.other_project_name }}
              </div>
              <div v-if="hasSupervisorHoursWarning(day)" class="day-conflict">
                Already clocked in at
                {{
                  formatBusinessTime(
                    day.active_clock_in_at,
                    undefined,
                    sheet.timezone,
                  )
                }}
              </div>
            </template>
            <div v-else-if="isLeaveDay(day)" class="mobile-day-inputs">
              <label class="mobile-field leave-hours-field">
                <span>Leave hours</span>
                <b-form-input
                  :value="day.leave_hours"
                  type="number"
                  min="0"
                  max="40"
                  step="0.1"
                  size="sm"
                  placeholder="Hours"
                  @input="$emit('set-leave-hours', ri, di, $event)"
                />
              </label>
              <div v-if="leaveHoursErrorFor(day)" class="day-error">
                {{ leaveHoursErrorFor(day) }}
              </div>
            </div>
            <!-- Read-only in edit mode — see SupervisorWorkerTable. -->
            <div
              v-if="day && day.plant_entries && day.plant_entries.length"
              class="readonly-plant-list"
            >
              <div
                v-for="pe in day.plant_entries"
                :key="pe.id"
                class="readonly-plant-line"
              >
                {{ pe.plant_name }} · {{ pe.quantity }} × ${{
                  formatCurrency(pe.rate_snapshot)
                }}{{ pe.rate_type_snapshot === 'hourly' ? '/hr' : '/day' }} =
                ${{ formatCurrency(pe.claim_value) }}
              </div>
            </div>
          </div>
        </div>
        <div v-else class="mobile-offsite-note">
          Marked off site for this week
        </div>
      </div>
    </template>

    <!-- View mode -->
    <template v-else>
      <div
        v-for="row in viewRows"
        :key="'m-view-' + row.worker.id"
        class="worker-mobile-card"
      >
        <div class="worker-mobile-head">
          <div class="worker-identity">
            <div class="worker-name">
              {{ row.worker.full_name }}
            </div>
            <div
              v-if="row.worker.trade"
              class="worker-trade worker-mobile-trade"
            >
              {{ formatTrade(row.worker.trade) }}
            </div>
          </div>
          <div class="worker-mobile-badges">
            <span class="total-hours-badge">{{ rowTotal(row) }}</span>
            <b-badge
              pill
              :variant="zoneStatusVariant(rowZoneStatus(row))"
              class="on-site-badge"
            >
              {{ zoneStatusLabel(rowZoneStatus(row)) }}
            </b-badge>
          </div>
        </div>

        <div class="worker-mobile-days">
          <div v-for="(day, i) in row.days" :key="i" class="mobile-day-row">
            <div class="mobile-day-label">
              <span class="mobile-day-name">{{ weekDayHeaders[i].label }}</span>
              <span class="mobile-day-date">{{ weekDayHeaders[i].date }}</span>
            </div>
            <div
              v-if="day.present && day.start && day.finish"
              class="mobile-day-time"
              :class="{ 'mobile-day-time--night': isNightDay(day) }"
            >
              <span class="mobile-day-range">
                {{ day.start }} – {{ day.finish }}
              </span>
              <span v-if="isNightDay(day)" class="day-night-badge">
                Night
              </span>
              <span v-if="day.break_minutes" class="mobile-day-break">
                {{ day.break_minutes }}m break
              </span>
              <span
                v-if="hasOtherProjectConflict(day)"
                class="day-conflict day-conflict-readonly"
                :title="day.other_project_name"
              >
                Also clocked in at {{ day.other_project_name }}
              </span>
              <span
                v-if="hasSupervisorHoursWarning(day)"
                class="day-conflict day-conflict-readonly"
              >
                Already clocked in at
                {{
                  formatBusinessTime(
                    day.active_clock_in_at,
                    undefined,
                    sheet.timezone,
                  )
                }}
              </span>
            </div>
            <div v-else-if="isLeaveDay(day)" class="mobile-leave-badge">
              {{ leaveTypeLabel(day.leave_type)
              }}<span v-if="leaveHoursLabel(day)">
                · {{ leaveHoursLabel(day) }}</span
              >
            </div>
            <span v-else class="empty-day-pill">Not added</span>
            <div
              v-if="day.plant_entries && day.plant_entries.length"
              class="readonly-plant-list"
            >
              <div
                v-for="pe in day.plant_entries"
                :key="pe.id"
                class="readonly-plant-line"
              >
                <template v-if="editingPlantEntryId === pe.id">
                  <b-form-input
                    :value="plantEntryEditQuantity"
                    type="number"
                    step="0.01"
                    min="0"
                    size="sm"
                    class="plant-entry-qty-input"
                    @input="
                      $emit('update:plantEntryEditQuantity', Number($event))
                    "
                    @keyup.enter="$emit('save-plant-entry-edit', pe)"
                    @keyup.esc="$emit('cancel-plant-entry-edit')"
                  />
                  <b-button
                    size="sm"
                    variant="link"
                    class="p-0 ml-1"
                    :disabled="plantEntrySaving"
                    @click="$emit('save-plant-entry-edit', pe)"
                  >
                    <b-icon-check />
                  </b-button>
                  <b-button
                    size="sm"
                    variant="link"
                    class="p-0 text-muted"
                    @click="$emit('cancel-plant-entry-edit')"
                  >
                    <b-icon-x />
                  </b-button>
                </template>
                <template v-else>
                  {{ pe.plant_name }} · {{ pe.quantity }} × ${{
                    formatCurrency(pe.rate_snapshot)
                  }}{{ pe.rate_type_snapshot === 'hourly' ? '/hr' : '/day' }} =
                  ${{ formatCurrency(pe.claim_value) }}
                  <b-button
                    v-if="canManagePlant"
                    variant="link"
                    size="sm"
                    class="p-0 ml-1 plant-entry-edit-btn"
                    @click="$emit('start-plant-entry-edit', pe)"
                  >
                    <b-icon-pencil font-scale="0.8" />
                  </b-button>
                  <b-badge
                    v-if="pe.is_amended"
                    variant="warning"
                    pill
                    class="ml-1 plant-amended-badge"
                    style="cursor: pointer"
                    @click="$emit('open-amendment-history', pe)"
                  >
                    Amended ×{{ pe.amendment_count }}
                  </b-badge>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  formatTrade,
  leaveTypeLabel,
  isLeaveDay,
  dayMinutes,
  formatCurrency,
  formatHours,
  isOvernight,
  isNightStart,
  dayError as sharedDayError,
  dayLeaveHours,
  leaveHoursError,
  formatBusinessTime,
} from '@/utils/timesheetHelpers'
import DatetimeInput from '@/components/timesheets/DatetimeInput.vue'

export default {
  name: 'SupervisorWorkerCards',

  components: { DatetimeInput },

  props: {
    sheet: { type: Object, required: true },
    editing: { type: Boolean, default: false },
    editRows: { type: Array, default: null },
    viewRows: { type: Array, required: true },
    weekDayHeaders: { type: Array, required: true },
    leaveTypeOptions: { type: Array, required: true },
    canManagePlant: { type: Boolean, default: false },
    editingPlantEntryId: { type: Number, default: null },
    plantEntryEditQuantity: { type: Number, default: null },
    plantEntrySaving: { type: Boolean, default: false },
    rowZoneStatus: { type: Function, required: true },
    zoneStatusVariant: { type: Function, required: true },
    zoneStatusLabel: { type: Function, required: true },
  },

  methods: {
    formatTrade,
    leaveTypeLabel,
    isLeaveDay,
    formatCurrency,
    formatBusinessTime,

    dayHoursLabel(day) {
      return formatHours(dayMinutes(day)) || null
    },

    isOvernightDay(day) {
      return !!day && day.present && isOvernight(day.start, day.finish)
    },
    isNightDay(day) {
      return !!day && day.present && !isLeaveDay(day) && isNightStart(day.start)
    },

    dayError(day) {
      return sharedDayError(day)
    },

    // Informational only — a worker can legitimately be booked on more than
    // one project the same day, so this never blocks save/submit.
    hasOtherProjectConflict(day) {
      return !!day && day.present && !!day.other_project_conflict
    },

    // Reverse of hasOtherProjectConflict — the tradie has an open mobile
    // clock-in for this day that this supervisor/BA entry didn't come from.
    // Informational only, never blocks save/submit.
    hasSupervisorHoursWarning(day) {
      return !!day && day.present && !!day.active_clock_in_conflict
    },

    leaveHoursLabel(day) {
      const h = dayLeaveHours(day)
      return h != null ? `${h}h` : null
    },

    leaveHoursErrorFor(day) {
      return leaveHoursError(day)
    },

    rowTotal(row) {
      const mins = row.days.reduce((sum, day) => sum + dayMinutes(day), 0)
      return mins > 0 ? formatHours(mins) : '-'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';
@import '~@/assets/scss/layouts/supervisor-worker-shared';

.worker-mobile-list {
  display: none;
}

@media (max-width: 1199.98px) {
  .worker-mobile-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
}

@media (min-width: 576px) and (max-width: 1199.98px) {
  .worker-mobile-days {
    display: grid;
    // 260px, not 215px: this card has to hold a native type="time" input at
    // DatetimeInput's 148px min-width floor plus its own padding — 215px
    // left too little margin in practice (Safari's native time control still
    // overflowed its box), so this gives real slack instead of chasing the
    // exact minimum again.
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 0.5rem;
    padding-top: 0.65rem;
    border-top: 1px solid #eef2f7;
  }

  .mobile-day-row,
  .mobile-day-row:last-child {
    padding: 0.5rem 0.6rem;
    border: 1px solid #eef2f7;
    border-radius: 10px;
    background: #fcfcfd;
  }

  .mobile-day-row-edit.mobile-day-row-active,
  .mobile-day-row-edit.mobile-day-row-active:last-child {
    margin-bottom: 0;
    border: 1px solid #dbeafe;
  }
}

.worker-mobile-card {
  min-width: 0;
  padding: 0.85rem 0.9rem;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.worker-mobile-card-muted {
  background: #f8fafc;
}

.worker-mobile-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem 0.6rem;
  flex-wrap: wrap;
}

.worker-mobile-head > .worker-identity,
.worker-mobile-head > .worker-on-site-toggle {
  flex: 1 1 9rem;
  min-width: 0;
}

.worker-mobile-card .worker-name,
.worker-mobile-card .worker-trade {
  overflow-wrap: break-word;
}

.worker-mobile-badges {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
  min-width: 0;
  flex-wrap: wrap;
}

.worker-mobile-trade {
  margin-top: 0.15rem;
}

.worker-mobile-card .worker-identity .worker-mobile-trade {
  margin-left: 0;
}

.worker-mobile-days {
  margin-top: 0.65rem;
  border-top: 1px solid #eef2f7;
}

.mobile-day-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.5rem 0;
  border-bottom: 1px dashed #eef2f7;
}

.mobile-day-row:last-child {
  border-bottom: none;
  padding-bottom: 0.15rem;
}

.mobile-day-label {
  display: flex;
  flex-direction: column;
  min-width: 64px;
  line-height: 1.2;
}

.mobile-day-name {
  color: #374151;
  font-size: 0.74rem;
  font-weight: 800;
}

.mobile-day-date {
  color: #9ca3af;
  font-size: 0.68rem;
  font-weight: 600;
}

.mobile-day-time {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.1rem;
  padding: 0.35rem 0.6rem;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  background: #f0fdf4;
}

.mobile-day-range {
  color: #166534;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
}

.mobile-day-time--night {
  border-color: #c7d2fe;
  background: #eef2ff;
}

.mobile-day-time--night .mobile-day-range {
  color: #3730a3;
}

.day-night {
  margin-top: 0.15rem;
}

.day-night-badge {
  display: inline-block;
  flex-shrink: 0;
  padding: 0.05rem 0.4rem;
  border: 1px solid #c7d2fe;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 0.62rem;
  font-weight: 600;
}

.mobile-day-break {
  color: #64748b;
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1.2;
}

.mobile-day-row-edit {
  flex-direction: column;
  align-items: stretch;
}

.mobile-day-row-edit.mobile-day-row-active {
  padding: 0.6rem 0.55rem;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 72%);
  margin-bottom: 0.5rem;
}

.mobile-day-row-edit.mobile-day-row-active:last-child {
  margin-bottom: 0;
}

.mobile-day-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
}

.mobile-day-add {
  width: auto;
  min-height: 34px;
  padding: 0.3rem 0.75rem;
}

// Stacked column, not a grid — a multi-column auto-fit grid here was
// computing its track width independently of DatetimeInput's min-width
// floor for type="time" (see the comment there), and CSS Grid doesn't grow
// an explicit-length minmax() track to fit oversized nested content; it just
// lets the overflow spill into the next card. One field per row always gets
// the card's full actual width instead, matching how the desktop table's
// .day-fields (SupervisorWorkerTable) already lays these out with no such
// issue.
.mobile-day-inputs {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-top: 0.5rem;
}

.mobile-field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin: 0;
  min-width: 0;
}

.mobile-field > span {
  color: #64748b;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.mobile-day-inputs /deep/ .datetime-input,
.mobile-day-inputs /deep/ .form-control {
  height: 34px;
  min-height: 34px;
  border-color: #cbd5e1;
  border-radius: 8px;
  color: #0f172a;
  font-size: 0.78rem;
  font-weight: 600;
}

// The calendar/clock icon is decorative — DatetimeInput's wrapper opens the
// native picker on click anywhere in the field, not just the icon — so drop
// it here and give that reclaimed width straight to the time text instead
// of reserving ~26px it doesn't need in this already-tight card.
// input[type='time'] (not just .datetime-input) so this beats
// DatetimeInput's own same-specificity rule reserving 22px for the icon —
// a bare .datetime-input override here would lose that specificity fight.
.mobile-day-inputs /deep/ input[type='time'].datetime-input {
  padding-right: 10px;
}

.mobile-day-inputs /deep/ .datetime-icon {
  display: none;
}

.mobile-day-row-edit .day-hours {
  margin-top: 0.4rem;
}

.mobile-offsite-note {
  margin-top: 0.65rem;
  padding: 0.5rem 0.65rem;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  color: #94a3b8;
  font-size: 0.74rem;
  font-weight: 700;
  text-align: center;
}

.mobile-day-row-leave {
  background: $leave-bg;
  box-shadow: inset 4px 0 0 $leave-border;
}

.mobile-leave-badge {
  padding: 0.3rem 0.5rem;
  border-radius: 8px;
  background: #ffffff;
  color: $leave-text;
  font-size: 0.75rem;
  font-weight: 700;
}

.worker-identity {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.worker-on-site-toggle {
  margin-bottom: 0;
}

.worker-name {
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 800;
  line-height: 1.25;
}

.worker-trade {
  margin-left: 1.55rem;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.35;
}

.day-hours {
  margin-top: 0.1rem;
  padding: 0.35rem 0.45rem;
  border-radius: 8px;
  background: #f8fafc;
  color: #166534;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.25;
}
</style>
